import {InputGroupModule} from 'primeng/inputgroup';
import {InputGroupAddonModule} from 'primeng/inputgroupaddon';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CheckboxModule} from 'primeng/checkbox';
import {DropdownModule} from 'primeng/dropdown';
import {NgIf} from '@angular/common';
import {InputTextModule} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {MessageModule} from 'primeng/message';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ComponentsModule} from '../components.module';
import {forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {
    AbstractStoreConf,
    ConfiguratorFileConnectionService,
    FileConnectionOptionDTO,
    FtpStoreConf,
    S3StoreConf,
    SharedFileExtractConf
} from '@tj/tj-dmp-lib-api';

/**
 * Sépare un chemin enregistré (« exports/clients.csv ») en dossier et nom de fichier. Le moteur lit le chemin
 * complet dans `fileName` / `filename` : `joinPath` le reconstitue à l'identique.
 */
export function splitPath(path: string | null | undefined): { directory: string; fileName: string } {
    if (!path) {
        return {directory: '', fileName: ''};
    }
    const lastSlash = path.lastIndexOf('/');
    if (lastSlash < 0) {
        return {directory: '', fileName: path};
    }
    return {directory: lastSlash === 0 ? '/' : path.substring(0, lastSlash), fileName: path.substring(lastSlash + 1)};
}

export function joinPath(directory: string | null | undefined, fileName: string | null | undefined): string {
    const dir = (directory ?? '').trim();
    const name = (fileName ?? '').trim().replace(/^\/+/, '');
    if (!dir) {
        return name;
    }
    return (dir.endsWith('/') ? dir : dir + '/') + name;
}

/**
 * Récupération (import) ou dépôt (canal d'export) d'un fichier sur un serveur S3 ou FTP. Le serveur est toujours
 * une connexion préparée dans Paramétrage > Groupes d'affiliés : le job ne porte que le dossier et le nom du fichier.
 * Les jobs enregistrés avant les connexions gardent leurs identifiants intégrés tant qu'on n'en choisit pas une.
 */
@Component({
    selector: 'app-file-server-config',
    templateUrl: './file-server-config.component.html',
    imports: [InputGroupModule, InputGroupAddonModule, 
        CheckboxModule,
        ReactiveFormsModule,
        DropdownModule,
        NgIf,
        InputTextModule,
        ButtonModule,
        MessageModule,
        TranslateModule,
        ComponentsModule
    ],
    styleUrls: ['./file-server-config.component.scss']
})
export class FileServerConfigComponent implements OnInit, OnChanges {

    public form!: FormGroup;

    @Input() public set config(value: SharedFileExtractConf | null) {
        if (value) {
            this.patchFromConfig(value);
        }
    }
    @Output() public configChange = new EventEmitter<SharedFileExtractConf>();
    @Output() public dirtyStateChange = new EventEmitter<boolean>();
    @Output() public validStateChange = new EventEmitter<boolean>();
    @Output() public removeChange = new EventEmitter<boolean>();

    @Input() public tenantId: string;
    @Input() public idEtl: string;

    /** Affiliés du job (import ou export) : ils déterminent les connexions proposées. */
    @Input() public partners: string[] = [];

    /** Si renseigné, ne propose que les connexions de ces propriétaires (null = connexion du tenant). */
    @Input() public allowedConnectionPartners: (string | null)[];

    /** Connexions proposées à ce job (sans paramètre ni secret). */
    public connectionOptions: FileConnectionOptionDTO[] = [];
    public connectionOptionsLoaded = false;

    /** Canal d'export : ni planification, ni case « date » propre à l'import. */
    @Input() public onlyFormShareFile = false;

    @Input() public set store(store: AbstractStoreConf | null) {
        if (store) {
            this.localStore = store;
            this.patchFromConfigStore(store);
        }
    }

    @Output() public storeChange = new EventEmitter<AbstractStoreConf>();

    private localStore: AbstractStoreConf | null = null;

    /** Import enregistré avec identifiants intégrés : renvoyé tel quel tant qu'aucune connexion n'est choisie. */
    private legacyExtract: Pick<SharedFileExtractConf, 'confFtp' | 'confS3Client'> | null = null;

    /** Serveur saisi directement dans un ancien job (« FTP ftp.exemple.fr »), affiché pour aider à choisir la connexion. */
    public legacyServer: string | null = null;

    // Suffixe unique pour les id des champs : l'export affiche un formulaire par canal sur la même page.
    private static instanceCounter = 0;
    public readonly uid = `file-server-config-${FileServerConfigComponent.instanceCounter++}`;

    constructor(private fb: FormBuilder,
                private fileConnectionService: ConfiguratorFileConnectionService,
                private readonly translate: TranslateService) {}

    public get scheduleEnabled(): boolean {
        return !!this.form.get('enableSchedule')?.value;
    }

    /**
     * Connexion, dossier et nom de fichier affichés seulement si la planification est cochée (seule récupération
     * qui lit le serveur), ou toujours pour un canal d'export, qui n'a pas cette case.
     */
    public get showServerFields(): boolean {
        return this.onlyFormShareFile || this.scheduleEnabled;
    }

    public get usesConnection(): boolean {
        return !!this.form?.get('connectionId')?.value;
    }

    /** Ancien job dont les identifiants intégrés n'ont pas encore été remplacés par une connexion. */
    public get showLegacyNotice(): boolean {
        return !!this.legacyServer && !this.usesConnection;
    }

    /** Racine de la connexion choisie, devant le dossier : un dossier vide y dépose le fichier. */
    public get rootPrefix(): string {
        const option = this.connectionOptions.find(o => o.id === this.form?.get('connectionId')?.value);
        return option?.root ? `${option.root}/` : '/';
    }

    public get isFtpConnection(): boolean {
        const option = this.connectionOptions.find(o => o.id === this.form?.get('connectionId')?.value);
        return !!option && option.type !== 'S3';
    }

    public get connectionDropdownOptions(): { label: string; value: string }[] {
        const current = this.form?.get('connectionId')?.value;
        const options = this.connectionOptions
            .filter(option => !this.allowedConnectionPartners || this.allowedConnectionPartners.includes(option.partner || null))
            .map(option => ({
            label: `${option.name} (${option.type === 'S3' ? 'S3' : 'FTP'}${option.partner ? ' · ' + option.partner : ''})`,
            value: option.id
        }));
        // Connexion supprimée ou hors périmètre : on la garde visible, signalée, plutôt que de la perdre en silence.
        if (current && this.connectionOptionsLoaded && !options.some(option => option.value === current)) {
            options.unshift({label: this.translate.instant('fileConnections.picker.unavailable'), value: current});
        }
        return options;
    }

    public get selectedConnectionUnavailable(): boolean {
        const current = this.form?.get('connectionId')?.value;
        return !!current && this.connectionOptionsLoaded && !this.connectionOptions.some(option => option.id === current);
    }

    public ngOnInit(): void {
        this.init();
        this.updateRequiredFields();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.tenantId || changes.partners || changes.allowedConnectionPartners) {
            this.loadConnectionOptions();
        }
    }

    private loadConnectionOptions(): void {
        if (!this.tenantId) {
            return;
        }
        const scopes: (string | undefined)[] = this.allowedConnectionPartners
            ? this.allowedConnectionPartners.map(p => p ?? undefined)
            : (this.partners?.length ? this.partners : [undefined]);
        forkJoin(scopes.map(partner =>
            this.fileConnectionService.getFileConnectionOptionsUsingGET(this.tenantId, partner)
                .pipe(catchError(() => of([] as FileConnectionOptionDTO[])))
        )).subscribe(results => {
            const seen = new Set<string>();
            this.connectionOptions = ([] as FileConnectionOptionDTO[]).concat(...results).filter(option => !seen.has(option.id) && !!seen.add(option.id));
            this.connectionOptionsLoaded = true;
        });
    }

    private init() {
        if (this.form === undefined) {
            this.form = this.fb.group({
                connectionId: [null],
                directory: [''],
                fileName: [''],
                dateInFileName: [false],
                enableSchedule: [false],
                cron: [false],
                moveFile: [false],
                targetDirectory: ['']
            });
            this.setupValidation();
            this.initEvent();
        }
    }

    private setupValidation(): void {
        this.form.get('enableSchedule')?.valueChanges.subscribe(() => this.updateRequiredFields());

        // targetDirectory conditionnelle
        this.form.get('moveFile')?.valueChanges.subscribe((enabled: boolean) => {
            const dirCtrl = this.form.get('targetDirectory');
            if (enabled) {
                dirCtrl?.setValidators(Validators.required);
            } else {
                dirCtrl?.clearValidators();
            }
            dirCtrl?.updateValueAndValidity();
        });
    }

    /**
     * Sans connexion ni nom de fichier, chaque exécution planifiée échoue dans l'engine (« file is empty ») ; un
     * canal d'export sans connexion n'a nulle part où déposer le fichier.
     */
    private updateRequiredFields(): void {
        const cronControl = this.form.get('cron');
        const connectionControl = this.form.get('connectionId');
        const fileNameControl = this.form.get('fileName');
        if (this.scheduleEnabled) {
            cronControl?.setValidators(Validators.required);
        } else {
            cronControl?.clearValidators();
        }
        // Canal d'export : sans nom, le dépôt échoue (S3) ou n'arrive jamais (FTP)
        if (this.scheduleEnabled || this.onlyFormShareFile) {
            fileNameControl?.setValidators(Validators.required);
        } else {
            fileNameControl?.clearValidators();
        }
        if (this.showServerFields) {
            connectionControl?.setValidators(Validators.required);
        } else {
            connectionControl?.clearValidators();
        }
        cronControl?.updateValueAndValidity();
        fileNameControl?.updateValueAndValidity();
        connectionControl?.updateValueAndValidity();
    }

    public getConfiguration(): SharedFileExtractConf {
        const value = this.form.getRawValue();
        const keepLegacy = !value.connectionId && this.legacyExtract;
        return {
            typeObject: 'SharedFileExtractConf',
            fileName: joinPath(value.directory, value.fileName),
            patternFileNameWithDate: value.dateInFileName,
            enableSchedule: value.enableSchedule,
            cron: value.enableSchedule ? value.cron : undefined,
            moveFile: value.moveFile,
            targetDirectory: value.moveFile ? value.targetDirectory : undefined,
            connectionId: value.connectionId || undefined,
            confS3Client: keepLegacy ? this.legacyExtract.confS3Client : undefined,
            confFtp: keepLegacy ? this.legacyExtract.confFtp : undefined
        };
    }

    private patchFromConfig(config: SharedFileExtractConf): void {
        this.init();
        const hasLegacyServer = !config.connectionId && (!!config.confFtp || !!config.confS3Client);
        this.legacyExtract = hasLegacyServer ? {confFtp: config.confFtp, confS3Client: config.confS3Client} : null;
        this.legacyServer = !hasLegacyServer ? null
            : config.confS3Client ? `S3 ${config.confS3Client.bucketName || config.confS3Client.endpoint || ''}`.trim()
            : `FTP ${config.confFtp.host || ''}`.trim();
        const path = splitPath(config.fileName);
        this.form.patchValue({
            connectionId: config.connectionId ?? null,
            directory: path.directory,
            fileName: path.fileName,
            dateInFileName: config.patternFileNameWithDate,
            enableSchedule: config.enableSchedule,
            cron: config.cron || '',
            moveFile: config.moveFile === undefined ? false : config.moveFile,
            targetDirectory: config.targetDirectory || ''
        });
        this.updateRequiredFields();
    }

    private patchFromConfigStore(config: AbstractStoreConf): void {
        this.init();
        const hasLegacyServer = !config.connectionId
            && ((config.typeObject === 'S3StoreConf' && !!(config as S3StoreConf).confS3Client)
                || (config.typeObject === 'FtpStoreConf' && !!(config as FtpStoreConf).host));
        this.legacyServer = !hasLegacyServer ? null
            : config.typeObject === 'S3StoreConf'
                ? `S3 ${(config as S3StoreConf).confS3Client.bucketName || (config as S3StoreConf).confS3Client.endpoint || ''}`.trim()
                : `FTP ${(config as FtpStoreConf).host}`;
        const path = splitPath(config.filename);
        this.form.patchValue({
            connectionId: config.connectionId ?? null,
            directory: path.directory,
            fileName: path.fileName,
            dateInFileName: config.patternFileNameWithDate || false
        });
        this.updateRequiredFields();
    }

    private initEvent() {
        setTimeout(() =>
            this.form.statusChanges.subscribe(status => {
                this.validStateChange.emit(status === 'VALID');
            })
        );

        this.form.valueChanges.subscribe(() => {
            if (this.form.valid && this.form.dirty) {
                const config = this.getConfiguration();
                this.configChange.emit(config);
                if (this.store) {
                    this.storeChange.emit(this.store);
                }
            }
            // Émettre l'état dirty à chaque changement
            this.dirtyStateChange.emit(this.form.dirty);
        });
    }

    public isValid(): boolean {
        return this.form.valid;
    }

    /** Affiche les erreurs (bordure rouge) des champs invalides, même jamais modifiés. */
    public showErrors(): void {
        Object.values(this.form.controls).forEach(control => {
            control.markAsDirty();
            control.markAsTouched();
        });
    }

    public removeConnection(): void {
        this.removeChange.emit(true);
    }

    /**
     * Canal d'export à enregistrer. Son type (S3 ou FTP) est celui de la connexion choisie ; un ancien canal sans
     * connexion est renvoyé tel quel (identifiants masqués : le serveur conserve ceux qu'il a en base).
     */
    public getStore(): AbstractStoreConf {
        if (!this.onlyFormShareFile) {
            throw new Error('cas non possible');
        }
        const value = this.form.getRawValue();
        const common = {
            filename: joinPath(value.directory, value.dateInFileName ? (value.fileName ?? '').replace(/\//g, '-') : value.fileName),
            patternFileNameWithDate: value.dateInFileName
        };
        if (!value.connectionId) {
            return {...this.localStore, ...common};
        }
        const option = this.connectionOptions.find(o => o.id === value.connectionId);
        const isS3 = option ? option.type === 'S3' : this.localStore?.typeObject === 'S3StoreConf';
        return isS3
            ? {typeObject: 'S3StoreConf', connectionId: value.connectionId, ...common} as S3StoreConf
            : {typeObject: 'FtpStoreConf', connectionId: value.connectionId, ...common} as FtpStoreConf;
    }
}
